import { Container, Divider, IconButton, Stack } from '@mui/material';
import BasketProfile from 'components/Home/BasketProfileContainer';
import Button from 'components/Common/Button';
import BasketDetailContainer from 'components/Home/BasketDetailContainer';
import BasketActivityContainer from 'components/Home/BasketActivityContainer';
import Tab from 'components/Common/Tab';
import EditIcon from '@mui/icons-material/Edit';
import MenuList from 'components/Common/MenuList';

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'constants/routes';
import MuiLoader from 'components/Common/Loader';
import { deleteBasket } from 'services/deleteBasket';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityDetail, fetchBasketDetail } from 'redux/slices/baskets/basketDetailsSlice';
import { AppDispatch, RootState } from 'redux/store';
import { notify } from 'components/Common/Toast';
import DownloadCSV from 'components/CSV/DownloadCSV';
import DeleteModal from 'components/Common/DeleteModal';

const csvHeaders = [
    { label: 'Date', key: 'date' },
    { label: 'Time', key: 'time' },
    { label: 'From', key: 'name' },
    { label: 'Username', key: 'username' },
    { label: 'Status', key: 'status' },
    { label: 'Total', key: 'amount' },
];

function BasketDetailActive() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [tab, setTab] = useState('details');
    const [deleteLoading, setDelLoading] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const { basketDetails, activityDetailData, loading } = useSelector((state: RootState) => state?.baskets?.details);

    const { id } = useParams();
    const { status } = basketDetails || {};
    const isSuspended = status === 'SUSPEND';
    const isInactive = status === 'PENDING' || status === 'CLOSED';

    useEffect(() => {
        if (id) {
            if (tab === 'details') {
                dispatch(fetchBasketDetail(id));
            } else {
                dispatch(fetchActivityDetail(id));
            }
        }
    }, [id, tab, dispatch]);

    const setTabValue = (value: string) => {
        setTab(value);
    };

    const navigationHandler = (row: any) => {
        navigate(routes.transaction.main.replace(':id', row?.paymentId?.toString()));
    };

    const handleDeleteBasket = () => {
        setDeleteModal(true);
    };

    const onDelete = async () => {
        try {
            setDelLoading(true);
            const responseDelete: any = await deleteBasket(id);
            if (responseDelete?.code === 200) {
                notify('Basket deleted successfully!', 'success');
                navigate(routes.home.main);
                return responseDelete;
            }
        } catch (error) {
            console.error('delete Basket error', error);
        } finally {
            setDelLoading(false);
        }
    };

    const editBasketNavigationHandel = (id: string | undefined) => {
        if (id) {
            navigate(routes.home.basketUpdate.replace(':id', id.toString()));
        } else {
            console.error('ID is undefined');
        }
    };

    const handleWithdrawClick = () => {
        id && navigate(routes.home.basketwithdrawbalance.replace(':id', id.toString()));
    };

    const disabledStatuses = ['SUSPEND', 'PENDING', 'CLOSED'];

    return (
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} justifyContent="space-evenly">
            <div>
                <Stack width="100%" alignItems="center">
                    <BasketProfile basket={basketDetails} />
                </Stack>

                {!(isSuspended || isInactive) && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, marginBottom: 20 }}>
                        <Button onClick={handleWithdrawClick} width={'23rem'} title="Withdraw Balance" />
                    </div>
                )}
            </div>
            <Container sx={{ backgroundColor: 'white', p: 2 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-around',
                    }}
                >
                    <Tab
                        tabs={[
                            { value: 'details', label: 'Details' },
                            { value: 'activity', label: 'Activity' },
                        ]}
                        tabValue={tab}
                        setTabValue={setTabValue}
                    />

                    {!isSuspended && (
                        <IconButton onClick={() => editBasketNavigationHandel(id)} sx={{ marginRight: 1 }}>
                            <EditIcon htmlColor="black" />
                        </IconButton>
                    )}
                    <MenuList
                        loading={deleteLoading}
                        mt={2}
                        menuItems={[
                            {
                                item: 'Create CSV File',
                                hide: tab !== 'activity',
                                render: () => (
                                    <DownloadCSV data={activityDetailData} headers={csvHeaders} fileName="Basket-activity">
                                        Create CSV File
                                    </DownloadCSV>
                                ),
                                disabled: !activityDetailData?.length,
                            },
                            {
                                item: 'Delete Basket',
                                onClick: () => handleDeleteBasket(),
                                loading: deleteLoading,
                                disabled: status ? disabledStatuses.includes(status) : false,
                            },
                            // You can add more menu items with their onClick functions here
                        ]}
                    />
                </div>
                <Divider />
                <MuiLoader loading={loading} absolute />
                <Container
                    component={
                        tab === 'details'
                            ? () => <BasketDetailContainer basketDetails={basketDetails} />
                            : () => <BasketActivityContainer data={activityDetailData} navigationHandler={navigationHandler} />
                    }
                />
            </Container>
            <DeleteModal
                isOpen={deleteModal}
                title="Delete Basket"
                onClose={() => setDeleteModal(false)}
                onDelete={onDelete}
                loading={deleteLoading}
                description="Are you sure you want to delete the selected Basket!"
            />
        </Stack>
    );
}

export default BasketDetailActive;
